import { useState } from "react";
import { useNavigate, Link, useLocation } from "react-router-dom";
import { useAuth, useTheme } from "@/App";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { Eye, EyeOff, Mail, Lock, User, ArrowLeft, Loader2, Check, Phone, X, Sun, Moon } from "lucide-react";
import PhoneInputWithCode, { formatPhoneNumber } from "../components/PhoneInputWithCode";
import VerificationModal from "@/components/VerificationModal";

const RegisterPage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { register, updateUser } = useAuth();
  const { theme, toggleTheme } = useTheme();
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [email, setEmail] = useState("");
  const [phone, setPhone] = useState("");
  const [phoneCountryCode, setPhoneCountryCode] = useState("+1|US");
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  
  const [consentAgreed, setConsentAgreed] = useState(null);
  const [showDisagreeModal, setShowDisagreeModal] = useState(false);
  
  const [showVerificationModal, setShowVerificationModal] = useState(false);
  const [verificationToken, setVerificationToken] = useState(null);
  const [registeredEmail, setRegisteredEmail] = useState("");

  const isDark = theme === 'dark';

  const passwordChecks = [
    { label: "At least 8 characters", valid: password.length >= 8 },
    { label: "Contains a number", valid: /\d/.test(password) },
    { label: "Contains uppercase", valid: /[A-Z]/.test(password) },
  ];

  const handleConsentChange = (agreed) => {
    setConsentAgreed(agreed);
    if (!agreed) {
      setShowDisagreeModal(true);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!firstName || !lastName || !email || !password) {
      toast.error("Please fill in all fields");
      return;
    }

    if (password !== confirmPassword) {
      toast.error("Passwords do not match");
      return;
    }

    if (password.length < 8) {
      toast.error("Password must be at least 8 characters");
      return;
    }

    if (consentAgreed !== true) {
      toast.error("Please agree to the consent terms to create an account");
      return;
    }

    setLoading(true);
    try {
      const userData = await register(email, password, firstName, lastName, {
        consent_call: true,
        consent_text: true,
        consent_email: true,
        phone: phone
      });
      
      setRegisteredEmail(email);
      setVerificationToken(userData.verification_token);
      
      toast.success("Account created! Please verify your email.");
      setShowVerificationModal(true);
      
      setTimeout(() => {
        const params = new URLSearchParams({
          token: userData.verification_token,
          email: email,
          firstName: firstName
        });
        const mockEmailUrl = `${window.location.origin}/mock-email?${params.toString()}`;
        window.open(mockEmailUrl, "_blank", "width=900,height=800");
      }, 500);
      
    } catch (error) {
      console.error("Register error:", error);
      toast.error(error.response?.data?.detail || "Registration failed. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const handleVerificationComplete = () => {
    setShowVerificationModal(false);
    updateUser({ email_verified: true });
    toast.success("Email verified! Welcome to USA.edu!");
    navigate("/dashboard", { state: { newUser: true, program: location.state?.program } });
  };

  const allFieldsFilled = firstName && lastName && email && phone && password && confirmPassword && password === confirmPassword;

  return (
    <div className={`min-h-screen flex ${isDark ? 'bg-[#0A0E14]' : 'bg-gray-50'}`}>
      <VerificationModal
        isOpen={showVerificationModal}
        email={registeredEmail}
        firstName={firstName}
        verificationToken={verificationToken}
        onVerified={handleVerificationComplete}
        canClose={false}
      />

      {showDisagreeModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center">
          <div className="absolute inset-0 bg-black/70 backdrop-blur-sm" onClick={() => setShowDisagreeModal(false)} />
          <div className={`relative rounded-2xl p-8 max-w-md mx-4 animate-slide-up ${isDark ? 'bg-[#11161F] border border-white/10' : 'bg-white border border-gray-200 shadow-xl'}`}>
            <button
              onClick={() => setShowDisagreeModal(false)}
              className={`absolute top-4 right-4 transition-colors ${isDark ? 'text-slate-400 hover:text-white' : 'text-gray-400 hover:text-gray-700'}`}
            >
              <X className="w-5 h-5" />
            </button>
            <div className="text-center">
              <div className="w-16 h-16 rounded-full bg-[#FF9800]/10 flex items-center justify-center mx-auto mb-4">
                <Phone className="w-8 h-8 text-[#FF9800]" />
              </div>
              <h3 className={`text-xl font-semibold mb-3 ${isDark ? 'text-white' : 'text-gray-900'}`}>Complete Your Application by Phone</h3>
              <p className={`mb-6 ${isDark ? 'text-slate-400' : 'text-gray-600'}`}>
                Please call us to complete the application process:
              </p>
              <a 
                href="tel:855-481-9203"
                className="inline-block text-2xl font-bold text-[#00677F] hover:text-[#A1D8E0] transition-colors mb-6"
              >
                855-481-9203
              </a>
              <p className={`text-sm ${isDark ? 'text-slate-500' : 'text-gray-500'}`}>
                Our enrollment advisors are available to assist you.
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Left side - Visual */}
      <div className="hidden lg:flex flex-1 items-center justify-center p-12 relative overflow-hidden" data-dark-section>
        <div className={`absolute inset-0 ${isDark ? 'bg-gradient-to-br from-[#002855]/50 to-[#0A0E14]' : 'bg-gradient-to-br from-[#00677F] to-[#135163]'}`} />
        <div className={`absolute top-1/3 left-1/4 w-96 h-96 rounded-full blur-[100px] ${isDark ? 'bg-[#00677F]/10' : 'bg-white/10'}`} />
        <div className={`absolute bottom-1/3 right-1/4 w-96 h-96 rounded-full blur-[100px] ${isDark ? 'bg-[#7B68EE]/10' : 'bg-[#A1D8E0]/20'}`} />
        
        <div className="relative z-10 max-w-lg">
          <h2 className="font-['Montserrat'] text-5xl font-bold mb-6 leading-tight text-white">
            Begin Your Healthcare Career Journey
          </h2>
          <p className={`text-lg mb-8 ${isDark ? 'text-slate-400' : 'text-white/80'}`}>
            Create your account to start your application to the University of St. Augustine for Health Sciences.
          </p>
          
          <div className="space-y-4">
            {[
              "AI-powered application assistance",
              "Real-time progress tracking",
              "Secure document uploads",
              "24/7 support access"
            ].map((feature, i) => (
              <div key={i} className="flex items-center gap-3">
                <div className={`w-6 h-6 rounded-full flex items-center justify-center ${isDark ? 'bg-[#00677F]/20' : 'bg-white/20'}`}>
                  <Check className={`w-4 h-4 ${isDark ? 'text-[#00677F]' : 'text-white'}`} />
                </div>
                <span className={isDark ? 'text-slate-300' : 'text-white/90'}>{feature}</span>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Right side - Form */}
      <div className="flex-1 flex items-center justify-center p-4 sm:p-8 overflow-y-auto">
        <div className="w-full max-w-md space-y-5 sm:space-y-6 animate-slide-up py-6 sm:py-8">
          {/* Top bar with back + theme toggle */}
          <div className="flex items-center justify-between">
            <Link 
              to="/"
              className={`inline-flex items-center gap-2 transition-colors ${isDark ? 'text-slate-400 hover:text-white' : 'text-gray-600 hover:text-gray-900'}`}
              data-testid="back-to-home-link"
            >
              <ArrowLeft className="w-4 h-4" />
              Back to Home
            </Link>
            <button
              onClick={toggleTheme}
              className={`p-2 rounded-full transition-colors ${isDark ? 'text-slate-400 hover:text-white hover:bg-white/5' : 'text-gray-600 hover:text-gray-900 hover:bg-gray-100'}`}
              data-testid="theme-toggle"
            >
              {isDark ? <Sun className="w-5 h-5" /> : <Moon className="w-5 h-5" />}
            </button>
          </div>

          {/* Logo */}
          <div className="flex items-center">
            <img 
              src={isDark
                ? "https://customer-assets.emergentagent.com/job_be4bb2aa-ca20-4b1a-9d54-431ad1ac86d0/artifacts/ie0vbsv3_primary.Horiz.2line.knockedout.whouter.pms315%2Bwhite.png"
                : "https://customer-assets.emergentagent.com/job_be4bb2aa-ca20-4b1a-9d54-431ad1ac86d0/artifacts/7kvxdoxy_primary.filled.whouter.pms315.png"
              }
              alt="University of St. Augustine for Health Sciences" 
              className="h-12 w-auto"
            />
          </div>

          {/* Header */}
          <div>
            <h2 className={`font-['Montserrat'] text-2xl sm:text-3xl font-bold ${isDark ? 'text-white' : 'text-gray-900'}`}>Create Account</h2>
            <p className={`mt-2 ${isDark ? 'text-slate-400' : 'text-gray-600'}`}>Start your application journey today</p>
          </div>

          {/* Form */}
          <form onSubmit={handleSubmit} className="space-y-5">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="firstName" className={isDark ? 'text-slate-300' : 'text-gray-700'}>First Name</Label>
                <div className="relative">
                  <User className={`absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 ${isDark ? 'text-slate-500' : 'text-gray-400'}`} />
                  <Input
                    id="firstName"
                    type="text"
                    placeholder="John"
                    value={firstName}
                    onChange={(e) => setFirstName(e.target.value)}
                    className={`pl-12 h-12 rounded-xl ${isDark ? 'bg-black/20 border-white/10 focus:border-[#A1D8E0] focus:ring-1 focus:ring-[#A1D8E0] text-white placeholder:text-slate-600' : 'bg-white border-gray-200 focus:border-[#00677F] focus:ring-1 focus:ring-[#00677F] text-gray-900 placeholder:text-gray-400'}`}
                    data-testid="register-firstname-input"
                  />
                </div>
              </div>
              <div className="space-y-2">
                <Label htmlFor="lastName" className={isDark ? 'text-slate-300' : 'text-gray-700'}>Last Name</Label>
                <Input
                  id="lastName"
                  type="text"
                  placeholder="Doe"
                  value={lastName}
                  onChange={(e) => setLastName(e.target.value)}
                  className={`h-12 rounded-xl ${isDark ? 'bg-black/20 border-white/10 focus:border-[#A1D8E0] focus:ring-1 focus:ring-[#A1D8E0] text-white placeholder:text-slate-600' : 'bg-white border-gray-200 focus:border-[#00677F] focus:ring-1 focus:ring-[#00677F] text-gray-900 placeholder:text-gray-400'}`}
                  data-testid="register-lastname-input"
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="email" className={isDark ? 'text-slate-300' : 'text-gray-700'}>Email Address</Label>
              <div className="relative">
                <Mail className={`absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 ${isDark ? 'text-slate-500' : 'text-gray-400'}`} />
                <Input
                  id="email"
                  type="email"
                  placeholder="you@example.com"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className={`pl-12 h-12 rounded-xl ${isDark ? 'bg-black/20 border-white/10 focus:border-[#A1D8E0] focus:ring-1 focus:ring-[#A1D8E0] text-white placeholder:text-slate-600' : 'bg-white border-gray-200 focus:border-[#00677F] focus:ring-1 focus:ring-[#00677F] text-gray-900 placeholder:text-gray-400'}`}
                  data-testid="register-email-input"
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="phone" className={isDark ? 'text-slate-300' : 'text-gray-700'}>Phone Number</Label>
              <PhoneInputWithCode
                value={phone}
                countryCode={phoneCountryCode}
                onPhoneChange={setPhone}
                onCodeChange={setPhoneCountryCode}
                testId="register-phone-input"
                theme={theme}
                inputClassName="rounded-xl"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="password" className={isDark ? 'text-slate-300' : 'text-gray-700'}>Password</Label>
              <div className="relative">
                <Lock className={`absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 ${isDark ? 'text-slate-500' : 'text-gray-400'}`} />
                <Input
                  id="password"
                  type={showPassword ? "text" : "password"}
                  placeholder="••••••••"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className={`pl-12 pr-12 h-12 rounded-xl ${isDark ? 'bg-black/20 border-white/10 focus:border-[#A1D8E0] focus:ring-1 focus:ring-[#A1D8E0] text-white placeholder:text-slate-600' : 'bg-white border-gray-200 focus:border-[#00677F] focus:ring-1 focus:ring-[#00677F] text-gray-900 placeholder:text-gray-400'}`}
                  data-testid="register-password-input"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className={`absolute right-4 top-1/2 -translate-y-1/2 transition-colors ${isDark ? 'text-slate-500 hover:text-white' : 'text-gray-400 hover:text-gray-700'}`}
                >
                  {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                </button>
              </div>
              
              {password && (
                <div className="space-y-2 mt-3">
                  {passwordChecks.map((check, i) => (
                    <div key={i} className="flex items-center gap-2 text-sm">
                      <div className={`w-4 h-4 rounded-full flex items-center justify-center ${check.valid ? 'bg-[#739600]' : isDark ? 'bg-slate-700' : 'bg-gray-300'}`}>
                        {check.valid && <Check className="w-3 h-3 text-white" />}
                      </div>
                      <span className={check.valid ? 'text-[#739600]' : isDark ? 'text-slate-500' : 'text-gray-500'}>{check.label}</span>
                    </div>
                  ))}
                </div>
              )}
            </div>

            <div className="space-y-2">
              <Label htmlFor="confirmPassword" className={isDark ? 'text-slate-300' : 'text-gray-700'}>Confirm Password</Label>
              <div className="relative">
                <Lock className={`absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 ${isDark ? 'text-slate-500' : 'text-gray-400'}`} />
                <Input
                  id="confirmPassword"
                  type={showPassword ? "text" : "password"}
                  placeholder="••••••••"
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  className={`pl-12 h-12 rounded-xl ${isDark ? 'bg-black/20 border-white/10 focus:border-[#A1D8E0] focus:ring-1 focus:ring-[#A1D8E0] text-white placeholder:text-slate-600' : 'bg-white border-gray-200 focus:border-[#00677F] focus:ring-1 focus:ring-[#00677F] text-gray-900 placeholder:text-gray-400'}`}
                  data-testid="register-confirm-password-input"
                />
              </div>
              {confirmPassword && password !== confirmPassword && (
                <p className="text-red-400 text-sm mt-1">Passwords do not match</p>
              )}
            </div>

            {/* Consent Agreement */}
            <div className="space-y-4 pt-2">
              <div className={`flex items-center gap-3 pb-2 border-b ${isDark ? 'border-white/[0.05]' : 'border-gray-200'}`}>
                <span className={`text-xs font-medium uppercase tracking-wider ${isDark ? 'text-slate-400' : 'text-gray-500'}`}>Consent Agreement</span>
              </div>
              
              <div className="space-y-3">
                {/* I Agree Card */}
                <button
                  type="button"
                  onClick={() => {
                    if (allFieldsFilled) {
                      handleConsentChange(true);
                    } else {
                      toast.error("Please fill in all fields correctly before agreeing");
                    }
                  }}
                  className={`w-full p-4 rounded-xl border transform transition-all duration-500 ease-out ${
                    consentAgreed === true
                      ? isDark
                        ? "border-[#739600]/50 bg-[#0d2818] scale-[1.02] shadow-[0_0_20px_rgba(40,167,69,0.2)]"
                        : "border-[#739600]/50 bg-[#739600]/5 scale-[1.02] shadow-md"
                      : !allFieldsFilled
                        ? isDark
                          ? "border-white/[0.05] bg-black/10 opacity-50"
                          : "border-gray-200 bg-gray-50 opacity-50"
                        : isDark
                          ? "border-white/[0.08] bg-black/20 hover:border-white/20 hover:scale-[1.01]"
                          : "border-gray-200 bg-white hover:border-gray-300 hover:scale-[1.01] shadow-sm"
                  }`}
                  data-testid="consent-agree-btn"
                >
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-4">
                      <div className={`w-10 h-10 rounded-full flex items-center justify-center transition-all duration-500 ${
                        consentAgreed === true ? "bg-[#739600]/20 scale-110" : isDark ? "bg-slate-700/50" : "bg-gray-100"
                      }`}>
                        <Check className={`w-5 h-5 transition-all duration-500 ${consentAgreed === true ? "text-[#739600]" : isDark ? "text-slate-400" : "text-gray-400"}`} />
                      </div>
                      <div className="text-left">
                        <h4 className={`font-medium transition-colors duration-500 ${
                          consentAgreed === true ? "text-[#739600]" : isDark ? "text-white" : "text-gray-900"
                        }`}>
                          I Agree
                        </h4>
                        <p className={`text-sm ${isDark ? 'text-slate-400' : 'text-gray-500'}`}>
                          Receive calls, texts, and emails from enrollment advisors
                        </p>
                      </div>
                    </div>
                    <div className={`w-6 h-6 rounded-full border-2 flex items-center justify-center transition-all duration-500 ${
                      consentAgreed === true ? "border-[#739600] bg-[#739600] scale-110" : isDark ? "border-slate-500" : "border-gray-300"
                    }`}>
                      {consentAgreed === true && <Check className="w-4 h-4 text-white animate-[scale-in_0.3s_ease-out]" />}
                    </div>
                  </div>
                </button>

                {/* I Do Not Agree Card */}
                <button
                  type="button"
                  onClick={() => handleConsentChange(false)}
                  className={`w-full p-4 rounded-xl border transform transition-all duration-500 ease-out ${
                    consentAgreed === false
                      ? isDark
                        ? "border-[#00677F]/50 bg-[#0a1a20] scale-[1.02] shadow-[0_0_20px_rgba(0,180,216,0.2)]"
                        : "border-[#00677F]/50 bg-[#00677F]/5 scale-[1.02] shadow-md"
                      : isDark
                        ? "border-white/[0.08] bg-black/20 hover:border-white/20 hover:scale-[1.01]"
                        : "border-gray-200 bg-white hover:border-gray-300 hover:scale-[1.01] shadow-sm"
                  }`}
                  data-testid="consent-disagree-btn"
                >
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-4">
                      <div className={`w-10 h-10 rounded-full flex items-center justify-center transition-all duration-500 ${
                        consentAgreed === false ? "bg-[#00677F]/20 scale-110" : isDark ? "bg-slate-700/50" : "bg-gray-100"
                      }`}>
                        <Phone className={`w-5 h-5 transition-all duration-500 ${consentAgreed === false ? "text-[#00677F]" : isDark ? "text-slate-400" : "text-gray-400"}`} />
                      </div>
                      <div className="text-left">
                        <h4 className={`font-medium transition-colors duration-500 ${
                          consentAgreed === false ? "text-[#00677F]" : isDark ? "text-white" : "text-gray-900"
                        }`}>
                          I Do Not Agree
                        </h4>
                        <p className={`text-sm ${isDark ? 'text-slate-400' : 'text-gray-500'}`}>
                          Complete application by phone instead
                        </p>
                      </div>
                    </div>
                    <div className={`w-6 h-6 rounded-full border-2 flex items-center justify-center transition-all duration-500 ${
                      consentAgreed === false ? "border-[#00677F] bg-[#00677F] scale-110" : isDark ? "border-slate-500" : "border-gray-300"
                    }`}>
                      {consentAgreed === false && <Check className="w-4 h-4 text-white animate-[scale-in_0.3s_ease-out]" />}
                    </div>
                  </div>
                </button>
              </div>
              
              <p className={`text-xs leading-relaxed ${isDark ? 'text-slate-500' : 'text-gray-500'}`}>
                By clicking "I Agree", I consent to the University of St. Augustine for Health Sciences using automated technology and/or pre-recorded means to email, text, and/or call me at the phone number above regarding educational services. I understand that my consent is not a required condition to purchase a good or service. I am providing an e-signature confirming my consent and my agreement to USAHS'{" "}
                <a href="#" className="text-[#00677F] hover:underline">Privacy Policy</a> and{" "}
                <a href="#" className="text-[#00677F] hover:underline">Terms of Use</a>. Msg and data rates may apply.
              </p>
              
              <p className={`text-xs ${isDark ? 'text-slate-600' : 'text-gray-400'}`}>
                You can update these preferences at any time in your account settings.
              </p>
            </div>

            {consentAgreed === true && (
              <div className="animate-[fadeSlideUp_0.4s_ease-out]">
                <Button
                  type="submit"
                  disabled={loading}
                  className="w-full h-14 bg-[#00677F] hover:bg-[#135163] text-white rounded-xl text-lg font-medium shadow-[0_0_20px_rgba(0,180,216,0.3)] hover:shadow-[0_0_30px_rgba(0,180,216,0.5)] transition-all mt-4"
                  data-testid="register-submit-btn"
                >
                  {loading ? (
                    <>
                      <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                      Creating Account...
                    </>
                  ) : (
                    "Create Account"
                  )}
                </Button>
              </div>
            )}
          </form>

          {/* Login link */}
          <p className={`text-center ${isDark ? 'text-slate-400' : 'text-gray-600'}`}>
            Already have an account?{" "}
            <Link 
              to="/login" 
              className="text-[#00677F] hover:text-[#A1D8E0] font-medium transition-colors"
              data-testid="login-link"
            >
              Sign In
            </Link>
          </p>
        </div>
      </div>
    </div>
  );
};

export default RegisterPage;
